// @ts-check
/** @typedef {import('../types/api').Dress} Dress */
/** @typedef {import('../types/api').DressStats} DressStats */
/** @typedef {import('../types/api').DressTimeline} DressTimeline */
/** @typedef {import('../types/api').HubManager} HubManager */
/** @typedef {import('../types/api').PaginatedHubManagers} PaginatedHubManagers */
/** @typedef {import('../types/api').Circulation} Circulation */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const dressService = {
  /**
   * @returns {Promise<Dress[]>}
   */
  getDresses: async () => {
    const data = await api.get('/api/admin/dresses');
    assertArray(data, 'dressService.getDresses');
    return data;
  },

  /**
   * @param {string} id
   * @returns {Promise<Dress>}
   */
  getDress: async (id) => {
    const data = await api.get(`/api/admin/dresses/${id}`);
    assertShape(data, ['id'], 'dressService.getDress');
    return data;
  },

  /**
   * @param {Object} data
   * @returns {Promise<Dress>}
   */
  createDress: (data) => api.post('/api/admin/dresses', data),

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<Dress>}
   */
  updateDress: (id, data) => api.put(`/api/admin/dresses/${id}`, data),

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteDress: (id) => api.del(`/api/admin/dresses/${id}`),

  /**
   * @param {string} id
   * @returns {Promise<DressTimeline[]>}
   */
  getDressTimeline: async (id) => {
    const data = await api.get(`/api/admin/dresses/${id}/timeline`);
    // Backend returns a bare list of timeline entries (response_model=List[...]),
    // not an object with a top-level dress_id — assertShape(['dress_id']) threw on
    // every valid response, silently swallowed by the caller's try/catch and always
    // showing an empty timeline in the Dress Detail drawer.
    assertArray(data, 'dressService.getDressTimeline');
    return data;
  },

  /**
   * @returns {Promise<DressStats>}
   */
  getDressStats: async () => {
    const data = await api.get('/api/admin/dresses/stats');
    assertShape(data, ['total_dresses'], 'dressService.getDressStats');
    return data;
  },

  // Hub Managers

  /**
   * @param {Object} [params]
   * @returns {Promise<PaginatedHubManagers>}
   */
  getHubManagers: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    if (params.search) qs.append('search', params.search);
    const q = qs.toString();
    const data = await api.get(`/api/admin/hub-managers${q ? `?${q}` : ''}`);
    assertShape(data, ['items', 'total'], 'dressService.getHubManagers');
    return data;
  },

  /**
   * @param {Object} data
   * @returns {Promise<HubManager>}
   */
  createHubManager: (data) => api.post('/api/admin/hub-managers', data),

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<HubManager>}
   */
  updateHubManager: (id, data) => api.put(`/api/admin/hub-managers/${id}`, data),

  /**
   * @param {string} id
   * @returns {Promise<HubManager>}
   */
  getHubManager: async (id) => {
    const data = await api.get(`/api/admin/hub-managers/${id}`);
    assertShape(data, ['id'], 'dressService.getHubManager');
    return data;
  },

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteHubManager: (id) => api.del(`/api/admin/hub-managers/${id}`),

  // Circulation

  /**
   * @param {Object} data
   * @returns {Promise<Circulation>}
   */
  createCirculation: (data) => api.post('/api/admin/dress-circulation', data),

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<Circulation>}
   */
  updateCirculationStatus: (id, data) => api.patch(`/api/admin/dress-circulation/${id}/status`, data),

  /**
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  logContent: (id, data) => api.post(`/api/admin/dress-circulation/${id}/content`, data),
};

export default dressService;
